'use client';

import { useEffect, useState } from 'react';

import { freeServices } from './servicesLinks';
import { LinkProps, FeedbackFormSuccessProps } from './types';

import IconClose from '@/assets/icons/iconClose.svg';
import IconTelegram from '@/assets/icons/iconTelegramSquare.svg';
import IconWhatsApp from '@/assets/icons/iconWhatsAppSquare.svg';
import getImageUrl from '@/shared/api/getImageUrl';
import { BlurredCard } from '@/shared/components/blurredCard';
import { Button } from '@/shared/components/button';
import { LOCALES, SCREEN_SM } from '@/shared/constants/common';
import { classNames } from '@/shared/lib/classNames/classNames';

const Link = ({ href, children }: LinkProps) => (
  <a
    className="bg-custom-surface-contrast-low flex w-fit items-center justify-center rounded-3xl px-5 py-2 text-sm leading-[18px] font-light max-sm:min-w-20 max-sm:px-[10px] max-sm:py-[7px] max-sm:text-[10px] max-sm:leading-[14px]"
    href={`${href}`}
    target="_blank"
  >
    {children}
  </a>
);

export const FeedbackFormSuccess = ({
  onClose,
  translations,
  locale,
}: FeedbackFormSuccessProps) => {
  const ruLocale = locale === LOCALES.ru;
  const enLocale = locale === LOCALES.en;

  const [bgImageSrc, setBgImageSrc] = useState('');

  const [cardImageSrc, setCardImageSrc] = useState('');
  const [isMobile, setIsMobile] = useState<boolean>();

  useEffect(() => {
    if (typeof window === undefined) {
      return;
    }
    const handleResize = () => setIsMobile(window.innerWidth <= SCREEN_SM);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setBgImageSrc('');

    const fetchImageUrl = async () => {
      const result = await getImageUrl(
        isMobile
          ? '/images/feedbackFormSuccessBackgroundMobile.jpg'
          : '/images/feedbackFormSuccessBackground.jpg',
      );
      setBgImageSrc(result.imageUrl);
    };

    fetchImageUrl();
  }, [isMobile]);

  useEffect(() => {
    const fetchImageUrl = async () => {
      const result = await getImageUrl('/images/university.png');
      setCardImageSrc(result.imageUrl);
    };

    fetchImageUrl();
  }, [isMobile]);

  return (
    <div className="fixed inset-0 z-[100] flex h-full w-full items-center justify-center backdrop-brightness-50">
      <div
        className="bg-surface-base-secondary relative h-fit w-[1168px] overflow-auto rounded-[28px] bg-cover bg-no-repeat p-14 max-sm:h-full max-sm:w-full max-sm:rounded-none max-sm:bg-bottom max-sm:p-5"
        style={{
          backgroundImage: `url(${bgImageSrc})`,
        }}
      >
        <IconClose
          onClick={onClose}
          className="text-text-base-primary absolute top-8 right-8 h-8 w-8 cursor-pointer max-sm:top-5 max-sm:right-5"
        />
        <p className="mb-4 w-[540px] text-[2.75rem] leading-[3.25rem] font-medium tracking-[-0.02rem] max-sm:w-full max-sm:text-2xl max-sm:leading-7">
          {translations.formSuccessHeader}
        </p>
        <p className="mb-7 w-[420px] text-lg leading-6 font-medium tracking-[-0.01rem] max-sm:mb-6 max-sm:w-full max-sm:text-sm max-sm:font-normal">
          {translations.formSuccessDescription}
        </p>
        <p className="mb-6 text-base max-sm:hidden">
          {' '}
          {translations.formSuccessNeeded}
        </p>
        <div className="mb-8 flex flex-wrap gap-6 max-sm:mb-6 max-sm:gap-4">
          <BlurredCard
            className={classNames({
              cls: 'bg-surface-base-blur relative max-w-[359px] border border-white/15 p-6 max-sm:w-full max-sm:p-5',
              mods: {
                'w-[367px]': ruLocale || enLocale,
              },
            })}
          >
            <p className="mb-3 text-lg leading-6 font-medium tracking-[-0.01rem] max-sm:text-xl max-sm:leading-6 max-sm:font-normal">
              {translations.formSuccessApiKeyTitle}
            </p>
            <p className="mb-[100px] text-sm leading-[18px] font-normal max-sm:mb-[14px]">
              {translations.formSuccessApiKeyDescription}
            </p>
            <Link href={translations.formSuccessApiKeyTagKeyLink}>
              {translations.formSuccessApiKeyTagKey}
            </Link>
            {cardImageSrc && !isMobile && (
              <img
                className="rounded-br-20 absolute right-0 bottom-0 w-40"
                src={cardImageSrc}
                alt="cardImageSrc"
              />
            )}
          </BlurredCard>
          <BlurredCard
            className={classNames({
              cls: 'bg-surface-base-blur flex w-[579px] flex-wrap gap-x-3 gap-y-4 border border-white/15 p-6 max-sm:w-full max-sm:p-5',
              mods: {
                '!w-[353px]': ruLocale || enLocale,
              },
            })}
          >
            <p className="text-lg leading-6 font-medium tracking-[-0.01rem] max-sm:text-xl max-sm:leading-6 max-sm:font-normal">
              {translations.formSuccessFreeServicesTitle}
            </p>
            <div className="flex flex-wrap gap-x-3 gap-y-4">
              {freeServices.map(({ href, title }, index) => (
                <Link key={index} href={`${translations[href]}`}>
                  {translations[title]}
                </Link>
              ))}
            </div>
          </BlurredCard>
          {(ruLocale || enLocale) && (
            <div className="w-[285px] max-sm:w-full">
              <BlurredCard className="bg-surface-base-blur mb-6 flex flex-wrap gap-x-4 border border-white/15 p-6 max-sm:mb-4 max-sm:p-5">
                <p className="mb-4 text-lg leading-6 font-medium tracking-[-0.01rem] max-sm:text-xl max-sm:leading-6 max-sm:font-normal">
                  {translations.formSuccessDemoTitle}
                </p>
                <Link href={translations.formSuccessFreeServicesTagApiLink}>
                  {translations.formSuccessFreeServicesTagApi}
                </Link>
                <Link href={translations.formSuccessFreeServicesTag2GisLink}>
                  {translations.formSuccessFreeServicesTag2Gis}
                </Link>
              </BlurredCard>
              <BlurredCard className="bg-surface-base-blur text-text-base-inverse-primary flex flex-wrap gap-x-2 border border-white/15 p-6 max-sm:p-5">
                <p className="mb-4 w-full text-lg leading-6 font-medium tracking-[-0.01rem] text-white max-sm:text-xl max-sm:leading-6 max-sm:font-normal">
                  {translations.formSuccessQuestion}
                </p>
                <a
                  href={translations.formSuccessTelegramLink}
                  aria-label="telegram"
                >
                  <IconTelegram />
                </a>
                <a
                  href={translations.formSuccessWhatsapp}
                  aria-label="whatsapp"
                >
                  <IconWhatsApp />
                </a>
              </BlurredCard>
            </div>
          )}
        </div>
        <Button
          className="flex h-14 w-72 items-center justify-center text-base max-sm:w-full"
          variant="primary"
          onClick={onClose}
          ariaLabel={translations.formSuccessDone}
        >
          {translations.formSuccessDone}
        </Button>
      </div>
    </div>
  );
};
